/**
 * FeatureFlagGuard Component
 *
 * GW-1284: Route guard component that checks feature flag status before rendering children.
 * Redirects to specified fallback route when flag is disabled.
 * Shows skeleton during loading to prevent flash of hidden content.
 */

import { Navigate } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { KnownFeatureFlags } from '@gw/ui/types';

interface FeatureFlagGuardProps {
  /** The feature flag name to check */
  flagName: KnownFeatureFlags;
  /** Where to redirect when flag is disabled */
  fallbackRoute?: string;
  /** Component to show while loading (defaults to PageSkeleton) */
  loadingComponent?: React.ReactNode;
  /** Children to render when flag is enabled */
  children: React.ReactNode;
}

/**
 * Guards routes behind a feature flag.
 * Shows loading state, then either renders children or redirects.
 *
 * @example
 * ```tsx
 * <FeatureFlagGuard flagName="datasets_marketplace" fallbackRoute="/">
 *   <DatasetsPage />
 * </FeatureFlagGuard>
 * ```
 */
export function FeatureFlagGuard({
  flagName,
  fallbackRoute = '/',
  loadingComponent,
  children,
}: FeatureFlagGuardProps) {
  const { isEnabled, isLoading, hasLoaded } = useFeatureFlag(flagName);

  // Show loading state to prevent flash of hidden content
  if (isLoading || !hasLoaded) {
    return <>{loadingComponent ?? <PageSkeleton />}</>;
  }

  // Redirect when flag is disabled
  if (!isEnabled) {
    return <Navigate to={fallbackRoute} replace />;
  }

  // Render children when flag is enabled
  return <>{children}</>;
}
